import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ImageUp,
  KeyRound,
  Loader2,
  Save,
  UserPlus,
} from "lucide-react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import type { AccountDTO, BrandUserDTO } from "@app/shared";
import { api, brandLogoUrl } from "../lib/api";
import { useBrands } from "../lib/brand";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../components/Toast";
import {
  ArticleConfigEditor,
  CadenceEditor,
  LimitsEditor,
  ModulesEditor,
} from "../components/BrandConfigEditors";
import { IgConnect, WpConnect } from "../components/BrandConnections";
import { SocialConnections } from "../components/SocialConnections";

function msg(e: unknown): string {
  return e instanceof Error ? e.message : "Terjadi kesalahan";
}

/**
 * Admin "Konfigurasi Brand" page (`/brands/:key`). The full per-brand config:
 * personalization, logo, AI brand guide, cadence, article/SEO, and a brand
 * login. Reached from the brand list; admin-only.
 */
export function BrandDetail() {
  const { isAdmin } = useBrands();
  const { key = "" } = useParams();

  const brandsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: ({ signal }) => api.accounts(signal),
  });
  const brand = brandsQuery.data?.find((b) => b.key === key) ?? null;

  if (!isAdmin) return <Navigate to="/drafts" replace />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/brands"
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy"
        >
          <ArrowLeft className="h-4 w-4" />
          Semua brand
        </Link>
      </div>

      {brandsQuery.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-teal" />
        </div>
      ) : brand ? (
        <BrandConfigForm brand={brand} />
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-sm text-slate-500">
            Brand "{key}" tidak ditemukan.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** The editable configuration for one brand. */
function BrandConfigForm({ brand }: { brand: AccountDTO }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [displayName, setDisplayName] = useState(brand.displayName);
  const [handle, setHandle] = useState(brand.handle ?? "");
  const [accentColor, setAccentColor] = useState(brand.accentColor ?? "#33BEBA");
  const [brandGuide, setBrandGuide] = useState(brand.brandGuide ?? "");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // After an OAuth round-trip the API redirects back here with `?social=<platform>`.
  // Surface a toast, refresh the connection list, then drop the query param.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const social = searchParams.get("social");
    if (!social) return;
    toast.success(`Koneksi ${social} berhasil`);
    void qc.invalidateQueries({ queryKey: ["social-connections", brand.key] });
    searchParams.delete("social");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, toast, qc, brand.key]);

  const [logoBust, setLogoBust] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["accounts"] });

  const uploadLogo = useMutation({
    mutationFn: (file: File) => api.uploadBrandLogo(brand.key, file),
    onSuccess: () => {
      toast.success("Logo diunggah");
      setLogoBust((n) => n + 1);
      invalidate();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const removeLogo = useMutation({
    mutationFn: () => api.deleteBrandLogo(brand.key),
    onSuccess: () => {
      toast.success("Logo dihapus");
      setLogoBust((n) => n + 1);
      invalidate();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const save = useMutation({
    mutationFn: () =>
      api.updateBrand(brand.key, {
        displayName: displayName.trim(),
        handle: handle.trim() || null,
        accentColor: accentColor || null,
        brandGuide: brandGuide.trim() || null,
      }),
    onSuccess: () => {
      toast.success("Personalisasi disimpan");
      invalidate();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const addUser = useMutation({
    mutationFn: () =>
      api.createBrandUser(brand.key, { email: email.trim(), password }),
    onSuccess: (u) => {
      toast.success(`Login brand dibuat: ${u.email}`);
      setEmail("");
      setPassword("");
      void qc.invalidateQueries({ queryKey: ["brand-users", brand.key] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span
            className="inline-block h-3.5 w-3.5 rounded-full ring-1 ring-slate-300"
            style={{ backgroundColor: brand.accentColor ?? "#33BEBA" }}
          />
          {brand.displayName}
          <span className="font-mono text-xs font-normal text-slate-400">
            {brand.key}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Personalization */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_1fr_8rem]">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nama tampilan</Label>
            <Input
              id="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="handle">Handle IG</Label>
            <Input
              id="handle"
              placeholder="@acme"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="color">Warna aksen</Label>
            <div className="flex items-center gap-2">
              <input
                id="color"
                type="color"
                value={
                  /^#[0-9a-fA-F]{6}$/.test(accentColor) ? accentColor : "#33BEBA"
                }
                onChange={(e) => setAccentColor(e.target.value)}
                className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-slate-300 bg-white p-1"
                aria-label="Pilih warna"
              />
              <Input
                type="text"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="#33BEBA"
                maxLength={7}
                className="flex-1 font-mono"
                aria-label="Kode hex warna"
              />
            </div>
          </div>
        </div>

        {/* Logo */}
        <div className="space-y-1.5">
          <Label>Logo</Label>
          <div className="flex items-center gap-3">
            {brand.hasLogo ? (
              <img
                src={brandLogoUrl(brand.key, logoBust)}
                alt={`${brand.displayName} logo`}
                className="h-12 w-12 rounded-md border border-slate-200 bg-navy object-contain p-1"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-md border border-dashed border-slate-300 text-xs text-slate-400">
                —
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadLogo.mutate(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploadLogo.isPending}
            >
              {uploadLogo.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImageUp className="h-4 w-4" />
              )}
              {brand.hasLogo ? "Ganti logo" : "Upload logo"}
            </Button>
            {brand.hasLogo ? (
              <Button
                variant="ghost"
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => removeLogo.mutate()}
                disabled={removeLogo.isPending}
              >
                Hapus logo
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-slate-500">
            PNG/JPG/WEBP, maks 10 MB. Tampil di gambar & materi brand ini.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="guide">Panduan AI (brand guide)</Label>
          <Textarea
            id="guide"
            rows={8}
            placeholder="Voice, positioning, pilar konten, aturan kepatuhan… (Markdown). Kosong = pakai panduan global."
            value={brandGuide}
            onChange={(e) => setBrandGuide(e.target.value)}
            className="font-mono text-xs"
          />
          <p className="text-xs text-slate-500">
            Teks ini menjadi system prompt AI khusus brand ini. Kosongkan untuk
            memakai panduan global.
          </p>
        </div>

        <div>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={save.isPending || displayName.trim().length === 0}
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Simpan
          </Button>
        </div>

        {/* Per-brand config (collapsible) */}
        <ModulesEditor brand={brand} />
        <CadenceEditor brandKey={brand.key} />
        <ArticleConfigEditor brandKey={brand.key} />
        <LimitsEditor brand={brand} />

        {/* Connections (Instagram + WordPress) */}
        <IgConnect account={brand} />
        <WpConnect account={brand} />
        <SocialConnections brand={brand} />

        {/* Brand login */}
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="mb-3 text-sm font-medium text-navy">Buat login brand</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <Input
              type="email"
              placeholder="email@brand.com"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Password (min 8)"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              variant="primary"
              onClick={() => addUser.mutate()}
              disabled={
                addUser.isPending || !email.includes("@") || password.length < 8
              }
            >
              {addUser.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              Buat login
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Pengguna ini hanya bisa mengelola konten brand {brand.displayName}.
          </p>
        </div>

        <BrandUsers brand={brand} />
      </CardContent>
    </Card>
  );
}

/** Lists a brand's logins, each with an inline "reset password" action. */
function BrandUsers({ brand }: { brand: AccountDTO }) {
  const usersQuery = useQuery({
    queryKey: ["brand-users", brand.key],
    queryFn: ({ signal }) => api.brandUsers(brand.key, signal),
  });
  const users = usersQuery.data ?? [];

  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4">
      <p className="mb-3 text-sm font-medium text-navy">Login brand</p>
      {usersQuery.isLoading ? (
        <p className="text-xs text-slate-500">Memuat…</p>
      ) : users.length === 0 ? (
        <p className="text-xs text-slate-500">
          Belum ada login untuk brand ini.
        </p>
      ) : (
        <ul className="space-y-2">
          {users.map((u) => (
            <BrandUserRow key={u.id} brand={brand} user={u} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** A single login row + a collapsible admin reset-password form. */
function BrandUserRow({
  brand,
  user,
}: {
  brand: AccountDTO;
  user: BrandUserDTO;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");

  const reset = useMutation({
    mutationFn: () =>
      api.resetBrandUserPassword(brand.key, user.id, { password: pw }),
    onSuccess: () => {
      toast.success(
        `Password ${user.email} direset — user akan diminta membuat password baru saat login.`,
      );
      setOpen(false);
      setPw("");
      void qc.invalidateQueries({ queryKey: ["brand-users", brand.key] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  return (
    <li className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-navy">{user.email}</p>
          {user.mustChangePassword && (
            <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
              Menunggu ganti password
            </span>
          )}
        </div>
        <Button variant="outline" onClick={() => setOpen((v) => !v)}>
          <KeyRound className="h-4 w-4" />
          Reset password
        </Button>
      </div>
      {open && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
          <Input
            type="password"
            placeholder="Password baru (min 8)"
            autoComplete="new-password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
          />
          <Button
            variant="primary"
            onClick={() => reset.mutate()}
            disabled={reset.isPending || pw.length < 8}
          >
            {reset.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Simpan password"
            )}
          </Button>
        </div>
      )}
    </li>
  );
}
